/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import cn from "classnames";
import { useAllCollapsed } from "cms/common/context/all-collapsed";
import { ComponentAccordionBottomToggle } from "cms/components/layout/footer/component-accordion-bottom-toggle";
import { ComponentEditorHeader } from "cms/components/layout/header/component-editor-header";
import { ComponentReuseBanner } from "cms/components/layout/reuse/banner/component-reuse-banner";
import { useUpdateEffect } from "react-use";

/**
 * @type {import('react').FC<ComponentAccordionProps>}
 */
const ComponentAccordion = (props) => {
    const { children, expanded, setExpanded } = props;

    const { allCollapsed, clearCollapse } = useAllCollapsed();

    const toggleExpanded = () => {
        setExpanded((prev) => !prev);
        clearCollapse();
    };

    useUpdateEffect(() => {
        if (allCollapsed !== null) {
            setExpanded(!allCollapsed);
        }
    }, [allCollapsed]);

    const headerClass = cn("simplicity-composer-header", {
        closed: !expanded
    });

    return (
        <Accordion
            className='simplicity-composer-accordion-root'
            expanded={expanded}
        >
            <div className={headerClass}>
                <AccordionSummary
                    className='simplicity-composer-accordion-header'
                    aria-controls='simplicity-composer-body'
                    onClick={toggleExpanded}
                >
                    <ComponentEditorHeader>
                        {/* we're inside of the accordion, so the parent toggle will be triggered */}
                        <ComponentAccordionBottomToggle
                            show={!expanded}
                            direction='down'
                        />
                    </ComponentEditorHeader>
                </AccordionSummary>
            </div>

            <div className='simplicity-composer-body'>
                
                <ComponentReuseBanner />

                <AccordionDetails>
                    {children}
                </AccordionDetails>

                <ComponentAccordionBottomToggle
                    show={expanded}
                    onClick={toggleExpanded}
                    direction='up'
                />
            </div>
        </Accordion>
    );
};

export { ComponentAccordion };


/**
 * @typedef {object} ComponentAccordionProps
 * @property {boolean} expanded
 * @property {SetState<boolean>} setExpanded
 * @property {import('react').ReactNode} children
 */
